import { Option, Result } from '@sapphire/result';
import { match } from 'ts-pattern';

import { Survey } from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

export type SurveyLoadFailure = 'invalid-id' | 'load-failed';

export type SurveyLoadOutcome = Result<Survey, SurveyLoadFailure>;

export interface SurveyLoadSnapshot {
  readonly rootSurveyId: Nullable<number>;
  readonly survey: Option<Survey>;
  readonly error: Nullable<string>;
}

export function parseSurveyId(
  rawSurveyId: Nullable<string>,
): Result<number, SurveyLoadFailure> {
  return Option.from(rawSurveyId)
    .map(Number)
    .filter((surveyId) => Number.isInteger(surveyId) && surveyId > 0)
    .okOr('invalid-id' as const);
}

/**
 * `None` while the load is still in flight. A survey of another id, or the
 * not-yet-hydrated state, both read as pending: loaded means the store is
 * rooted at this id AND the root survey is present.
 */
export function surveyLoadOutcome(
  surveyId: number,
  snapshot: SurveyLoadSnapshot,
): Option<SurveyLoadOutcome> {
  return match({
    isLoaded: snapshot.rootSurveyId === surveyId && snapshot.survey.isSome(),
    hasFailed: !!snapshot.error,
  })
    .with({ isLoaded: true }, () =>
      snapshot.survey.map<SurveyLoadOutcome>((survey) => Result.ok(survey)),
    )
    .with({ hasFailed: true }, () =>
      Option.some<SurveyLoadOutcome>(Result.err('load-failed')),
    )
    .otherwise(() => Option.none);
}
